import { ExcludedStop } from './route-optimization.interface';

/**
 * Resuelve las paradas que el SERVICIO apartó por su coordenada al cliente que
 * el usuario reconoce, para poder decir *quién* faltó y *por qué*.
 *
 * Existe porque el resultado del recorrido se arma desde `order`, la lista de lo
 * que SÍ entró: una parada apartada simplemente no aparecía, y el cliente
 * desaparecía de la pantalla sin ninguna mención. Un pedido ausente sin
 * explicación es peor que un error visible — el usuario ni siquiera sabe que
 * tiene algo que revisar.
 *
 * Es una función pura para poder probarla con datos planos, como el resto de los
 * módulos de esta carpeta.
 */

/** Lo mínimo que se necesita de un cliente para nombrarlo. */
export interface NamedClient {
  _id: string;
  commercialName?: string;
  name?: string;
}

/** Una parada apartada, ya lista para mostrar. */
export interface ExcludedStopView {
  name: string;
  reason: string;
  help: string;
}

/** Traduce un motivo del contrato a su etiqueta y su acción sugerida. */
export type ReasonDescriber = (reason: string) => { label: string; help: string };

/**
 * @param excluded Lo que devolvió el servicio. `undefined` = cero apartadas: una
 *   versión anterior del servicio no manda el campo, y eso no es un error.
 * @param clients Los clientes cargados en pantalla.
 * @param describe Cómo se nombra cada motivo (la tabla vive en quien llama).
 */
export function resolveExcludedStops(
  excluded: ExcludedStop[] | undefined,
  clients: NamedClient[],
  describe: ReasonDescriber,
): ExcludedStopView[] {
  const byId = new Map(clients.map((c) => [c._id, c]));

  return (excluded ?? []).map((e) => {
    const client = byId.get(e.id);
    const { label, help } = describe(e.reason);
    return {
      // Si el cliente no está entre los cargados, el id es peor que un nombre
      // pero infinitamente mejor que omitir la fila: la parada se apartó igual.
      name: client?.commercialName || client?.name || e.id,
      reason: label,
      help,
    };
  });
}
